<template>
  <component :is="baseTemplate">
    <div class="w-100 h-100 pb-4">
      <b-container fluid class="pt-5 mt-5">
        <h2>{{ pageTitle }}</h2>
        <p class="text-muted">
          Select a date from the last 30 days to view the application log.
        </p>

        <b-alert :show="!isValidAppType" variant="danger">
          The requested application type is invalid.
        </b-alert>

        <template v-if="isValidAppType">
          <b-form-group
            label="Log date"
            label-for="admin-log-date"
            class="log-date-selector"
          >
            <b-form-datepicker
              id="admin-log-date"
              v-model="selectedDate"
              :min="minimumDate"
              :max="maximumDate"
              locale="en"
            />
          </b-form-group>

          <div v-if="loading" class="py-4 text-center">
            <b-spinner class="mr-2" />
            <span>Loading log...</span>
          </div>

          <b-alert :show="Boolean(errorMessage)" variant="danger">
            {{ errorMessage }}
          </b-alert>

          <div
            v-if="!loading && !errorMessage && logLines.length"
            class="log-content"
            aria-live="polite"
          >
            <div
              v-for="(line, index) in logLines"
              :key="index"
              class="log-line"
            >{{ line || " " }}</div>
          </div>

          <b-alert
            :show="!loading && !errorMessage && !logLines.length"
            variant="info"
          >
            No log content is available for this date.
          </b-alert>
        </template>
      </b-container>
    </div>
  </component>
</template>

<script>
import { HTTP } from "@/utilities/http-common";
import VDBaseTemplate from "@/layouts/VDBaseTemplate.vue";
import VTBaseTemplate from "@/layouts/VTBaseTemplate.vue";

function toLocalIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}


export default {
  name: "admin-logs",
  components: {
    VDBaseTemplate,
    VTBaseTemplate,
  },
  data() {
    return {
      selectedDate: "",
      minimumDate: "",
      maximumDate: "",
      content: "",
      loading: false,
      errorMessage: "",
      requestId: 0,
    };
  },
  computed: {
    appType() {
      return this.$route.query.app_type;
    },
    isValidAppType() {
      return ["vecdyn", "vectraits"].includes(this.appType);
    },
    baseTemplate() {
      return this.appType === "vectraits"
        ? "VTBaseTemplate"
        : "VDBaseTemplate";
    },
    pageTitle() {
      if (this.appType === "vectraits") {
        return "VecTraits Logs";
      }
      if (this.appType === "vecdyn") {
        return "VecDyn Logs";
      }
      return "Application Logs";
    },
    logLines() {
      return this.content ? this.content.split(/\r?\n/) : [];
    },
  },
  watch: {
    selectedDate() {
      this.fetchLogs();
    },
    appType() {
      this.fetchLogs();
    },
  },
  created() {
    this.setDates();
    this.fetchLogs();
  },
  methods: {
    setDates(){
      const today = new Date();
      const earliestDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 29
      );
      this.selectedDate = toLocalIsoDate(today);
      this.minimumDate = toLocalIsoDate(earliestDate);
      this.maximumDate = toLocalIsoDate(today);
    },
    fetchLogs() {
      const currentRequestId = ++this.requestId;
      this.content = "";
      this.errorMessage = "";

      if (!this.isValidAppType) {
        this.loading = false;
        return;
      }

      this.loading = true;
      HTTP.get("/api/admin-logs/", {
        params: {
          app_type: this.appType,
          date: this.selectedDate,
        },
      })
        .then((response) => {
          if (currentRequestId === this.requestId) {
            this.content = response.data.content || "";
          }
        })
        .catch((error) => {
          if (currentRequestId === this.requestId) {
            const data = error.response && error.response.data;
            this.errorMessage =
              (data && (data.detail || data.message)) ||
              "There was a problem loading the log.";
          }
        })
        .finally(() => {
          if (currentRequestId === this.requestId) {
            this.loading = false;
          }
        });
    },
  },
};
</script>

<style scoped>
.log-date-selector {
  max-width: 24rem;
}

.log-content {
  min-height: 12rem;
  max-height: calc(100vh - 18rem);
  overflow: auto;
  padding: 1rem;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  background-color: #f8f9fa;
  color: #212529;
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
    "Courier New", monospace;
  font-size: 0.875rem;
  line-height: 1.4;
  text-align: left;
}

.log-line {
  min-height: 1.4em;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
</style>
